import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/api/client";
import { useTranslation } from "react-i18next";
import NotificationBanner from "@/components/NotificationBanner";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Database } from "lucide-react";

const COLORS = ["#2563EB", "#16A34A", "#F59E0B", "#DC2626"];
const CAT_LABEL = { fuel: "ガソリン代", toll: "高速料金", meal: "食事手当", other: "その他" };

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState("");
  const [seedErr, setSeedErr] = useState("");

  const load = () => api.get("/reports/dashboard/admin").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runSeed = async () => {
    setSeeding(true); setSeedMsg(""); setSeedErr("");
    try {
      await api.post("/admin/seed-demo");
      setSeedMsg(t("admin.seedDone"));
      load();
    } catch (e) {
      setSeedErr(formatApiErrorDetail(e.response?.data?.detail) || t("admin.seedFailed"));
    } finally { setSeeding(false); }
  };

  const Stat = ({ label, value, cls, testId }) => (
    <div className={`card stat ${cls}`} data-testid={testId}><div className="stat-label">{label}</div><div className="stat-value">{value}</div></div>
  );

  const chartData = d ? Object.entries(d.monthly_expense_by_category || {}).map(([k, v]) => ({ name: CAT_LABEL[k] || k, value: v })) : [];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 className="page-title">{t("admin.dashboard")}</h1>
          <p className="page-sub">全体状況の概要</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <button
            className="btn btn-outline"
            onClick={runSeed}
            disabled={seeding}
            data-testid="seed-demo-btn"
            title={t("admin.seedDemoHint")}
          >
            <Database size={14} /> {seeding ? t("admin.seeding") : t("admin.seedDemo")}
          </button>
          {seedMsg && <div data-testid="seed-msg" style={{ color: "#16A34A", fontSize: 12, marginTop: 6 }}>{seedMsg}</div>}
          {seedErr && <div data-testid="seed-err" style={{ color: "#DC2626", fontSize: 12, marginTop: 6 }}>{seedErr}</div>}
        </div>
      </div>
      <NotificationBanner testIdPrefix="admin-dash-notif" style={{ marginBottom: 20 }} />
      <div className="grid grid-5" style={{ marginBottom: 20 }}>
        <Stat testId="dash-total-emp" label={t("admin.totalEmployees")} value={d?.total_employees ?? 0} cls="primary" />
        <Stat testId="dash-submitted" label={t("admin.submittedToday")} value={d?.attendance_submitted_today ?? 0} cls="success" />
        <Stat testId="dash-missing" label={t("admin.missing")} value={d?.missing_attendance ?? 0} cls="warn" />
        <Stat testId="dash-pending-att" label={t("admin.pendingAtt")} value={d?.pending_attendance_approvals ?? 0} cls="warn" />
        <Stat testId="dash-pending-exp" label={t("admin.pendingExp")} value={d?.pending_expense_approvals ?? 0} cls="danger" />
      </div>
      <div className="grid grid-2">
        <div className="card"><div className="card-body">
          <div className="card-title">{t("admin.monthlyExpenseSummary")} - 円</div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer><BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => `¥${Number(v).toLocaleString()}`} />
              <Bar dataKey="value" fill="#2563EB" radius={[6,6,0,0]} />
            </BarChart></ResponsiveContainer>
          </div>
        </div></div>
        <div className="card"><div className="card-body">
          <div className="card-title">経費構成比</div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer><PieChart>
              <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(x) => x.name}>
                {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => `¥${Number(v).toLocaleString()}`} />
            </PieChart></ResponsiveContainer>
          </div>
          <div style={{ textAlign: "center", fontWeight: 700 }}>{t("common.total")}: ¥{Number(d?.monthly_expense_total || 0).toLocaleString()}</div>
        </div></div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-body">
          <div className="card-title">{t("admin.employeeHoursTitle")}</div>
          <div className="table-wrap">
            <table className="table" data-testid="dash-emp-hours">
              <thead><tr>
                <th>{t("admin.empCode")}</th>
                <th>{t("admin.name")}</th>
                <th style={{ textAlign: "right" }}>{t("driver.workingHours")}</th>
                <th style={{ textAlign: "right" }}>{t("admin.reportDaysWorked")}</th>
              </tr></thead>
              <tbody>
                {(d?.employee_hours || []).map((row) => (
                  <tr key={row.employee_id}>
                    <td>{row.employee_code}</td>
                    <td>{row.name}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{row.monthly_hours} h</td>
                    <td style={{ textAlign: "right" }}>{row.days_worked}</td>
                  </tr>
                ))}
                {(!d?.employee_hours || d.employee_hours.length === 0) && (
                  <tr><td colSpan="4" style={{ textAlign: "center", color: "#64748B" }}>—</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
